import { getHeaders } from 'auth/auth';
import { api } from 'constants/constant';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const resolveTenantApiBase = () => api || '';

export const underwritingAPI = createApi({
  reducerPath: 'underwriting',
  tagTypes: [
    'underwritingResults',
    'pipelineReport',
    'archivedReports',
    'underwritingDesk',
    'underwritingActivity',
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: resolveTenantApiBase(),
    credentials: 'include',
  }),
  endpoints: (builder) => ({
    // Get underwriting results for an application
    getUnderwritingResults: builder.query({
      query: ({ applicationId }) => {
        const queryParams = applicationId ? `?application_id=${applicationId}` : '';
        return {
          url: `b2b/ai/underwriting${queryParams}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      // This prevents RTK Query from treating 404 as an error, since we want to handle "no results" gracefully
      validateStatus: (response, result) => {
        return response.status === 200 || response.status === 404;
      },
      // Transform the response to handle both successful and 404 cases
      transformResponse: (response, meta) => {
        // If the response is an error message (404), return an empty array
        if (meta?.response?.status === 404) {
          return [];
        }
        if (!response) {
          return [];
        }
        if (Array.isArray(response)) {
          return response;
        }
        const summaryPayload = response.summary || {};
        const analysisResult =
          response.analysis_result ||
          {
            ...summaryPayload,
            remarks: response.remarks || summaryPayload.remarks || [],
          };
        const normalized = {
          ...response,
          analysis_result: analysisResult,
          created_at: response.created_at || response.generated_at,
          file_names: response.file_names || [],
        };
        return [normalized];
      },
      keepUnusedDataFor: 3600, // Keep data for 1 hour
      providesTags: (result, error, { applicationId, userId }) => [
        { type: 'underwritingResults', id: `${applicationId}-${userId || 'none'}` }
      ],
    }),

    // Run the deterministic pipeline and store temp report in MongoDB
    runPipeline: builder.mutation({
      query: ({ applicationId, runToken }) => ({
        url: `b2b/loans/pre-underwriting/run-pipeline/${applicationId}`,
        method: 'POST',
        // The screen's id for this run. Stamped onto every progress blob the
        // run writes, so the poller below can tell them from the last run's.
        params: runToken ? { run_token: runToken } : {},
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'pipelineReport', id: applicationId },
      ],
    }),

    // The deterministic half of the report — verdict, metrics, levers, blockers
    // and per-category counts. Finishes in seconds and renders on its own, so it
    // is deliberately NOT bundled with the AI result that takes minutes.
    getPipelineReport: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loans/pre-underwriting/pipeline-report/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      // Matches the agent's half above. Moving between tabs on a borrower
      // unmounts this screen constantly, and on the 60-second default the
      // report was dropped while its sibling was not — so returning meant a
      // fetch in flight and the page rendering complete with the checks
      // missing. Age costs nothing here: the query revalidates on every mount,
      // and re-running the analysis invalidates the tag outright.
      keepUnusedDataFor: 3600,
      // A file that has never been run 404s. That is an empty state, not an error.
      validateStatus: (response) =>
        response.status === 200 || response.status === 404,
      transformResponse: (response, meta) =>
        meta?.response?.status === 404 ? null : response?.data ?? null,
      providesTags: (result, error, { applicationId }) => [
        { type: 'pipelineReport', id: applicationId },
      ],
    }),

    /* How far through a run the agent is. Polled only while one is in flight.

       The agent's half takes minutes and had one signal for all of it — the
       finished report — so the progress bar sat on the same step for the whole
       wait. This is the one real event in between: the income stage finishing.
       Never cached, because a stale answer here is worse than no answer.

       `runToken` is part of the query args deliberately, not just the URL: RTK
       keys its cache on the args, so a new run cannot read the previous run's
       entry even for the instant before the first request returns. That instant
       is what drew the last run's finished pills at the top of the next one. */
    getRunProgress: builder.query({
      query: ({ applicationId, runToken }) => ({
        url: `b2b/loans/pre-underwriting/run-progress/${applicationId}`,
        method: 'GET',
        // The server drops any progress blob belonging to a different run, so
        // this screen never has to reason about whose progress it is holding.
        params: runToken ? { run_token: runToken } : {},
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      keepUnusedDataFor: 0,
    }),

    // File the finished report in the documents store. Idempotent on the
    // agent's report version, so a re-fire returns the document already on
    // file rather than creating a second copy.
    archiveReport: builder.mutation({
      query: ({ applicationId }) => ({
        url: `b2b/loans/pre-underwriting/archive-report/${applicationId}`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'archivedReports', id: applicationId },
      ],
    }),

    // Past reports, newest first. Each row carries the document id the
    // existing documents download route takes.
    getArchivedReports: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loans/pre-underwriting/reports/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      validateStatus: (response) =>
        response.status === 200 || response.status === 404,
      transformResponse: (response, meta) =>
        meta?.response?.status === 404 ? [] : response?.data ?? [],
      providesTags: (result, error, { applicationId }) => [
        { type: 'archivedReports', id: applicationId },
      ],
    }),

    /* ---- Underwriting desk ------------------------------------------------
       Separate from everything above: those endpoints serve pre-underwriting,
       which is advisory. These serve the decision, which has state. */

    // Everything the underwriting tab renders in one call — decision, snapshot,
    // conditions, gate counts, loan terms and milestones. A file that has never
    // been submitted comes back with started:false rather than a 404.
    getUnderwritingDesk: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loans/underwriting/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
      ],
    }),

    // Opens the review against the current pre-underwriting run. Called again
    // later it re-bases onto a newer one without disturbing cleared work.
    startUnderwritingReview: builder.mutation({
      query: ({ applicationId }) => ({
        url: `b2b/loans/underwriting/start/${applicationId}`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    /* The four condition actions. Rejecting and waiving both close a turn but
       mean opposite things: a rejection leaves the condition owed, a waiver
       drops the requirement. Each invalidates the desk so the gate counts
       recount. */
    conditionAction: builder.mutation({
      query: ({ applicationId, conditionId, action, body }) => ({
        url:
          action === 'reassign'
            ? `b2b/loans/underwriting/${applicationId}/conditions/${conditionId}/owner`
            : `b2b/loans/underwriting/${applicationId}/conditions/${conditionId}/${action}`,
        method: action === 'reassign' ? 'PATCH' : 'POST',
        headers: getHeaders(),
        body: body || {},
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    /* Ask the rules whether the conditions on the sheet are still true.

       Not `startUnderwritingReview`. That re-bases the worklist onto a newer
       pre-underwriting run and retires everything the previous one raised,
       cleared rows included. This runs the deterministic rules against the
       loan as it stands now: a condition whose rule passes is cleared, one
       whose figures moved gains a revision, and everything else is left alone.

       Agent-sourced conditions are not touched — nothing here can re-read a
       document — and the response says how many were left out so the screen
       can say so rather than implying they were checked. */
    recheckConditions: builder.mutation({
      query: ({ applicationId, runToken }) => ({
        url: `b2b/loans/underwriting/${applicationId}/recheck-conditions`,
        method: 'POST',
        headers: getHeaders(),
        params: runToken ? { run_token: runToken } : {},
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    /* Step-by-step progress for a re-check in flight.

       The wait is the snapshot, not the rules — thirteen tables and the
       disclosure ledger, about nine seconds, against a millisecond for all
       eighty-two checks — so this is the same blob the pre-underwriting loader
       reads, under its own Redis key.

       `runToken` is part of the query args deliberately, not just the URL: RTK
       keys its cache on the args, so a second press cannot read the first
       press's entry even for the instant before its own first request returns.
       That instant is what draws the previous run's finished cards. */
    getRecheckProgress: builder.query({
      query: ({ applicationId, runToken }) => ({
        url: `b2b/loans/underwriting/${applicationId}/recheck-progress`,
        method: 'GET',
        params: runToken ? { run_token: runToken } : {},
        headers: getHeaders(),
      }),
      /* The whole payload, not just `pipeline`. A re-check is four phases now:
         the rules, then the agent's income stage, then its document stage,
         then the report. `pipeline` is the first phase step by step out of
         Redis; `income` and `documents` are the agent's two markers out of
         Mongo, and each one moves the stepper on a node. */
      transformResponse: (response) => response?.data ?? null,
      keepUnusedDataFor: 0,
    }),

    /* The second half of a re-check: compare the agent's fresh review against
       the conditions it raised.

       Fired by the screen once the assistant's turn finishes, because nothing
       on the server is watching for it.

       `since` is when the run started, and the endpoint refuses a report older
       than it — which doubles as the "has it landed yet" check, so a 409 here
       means not yet rather than wrong. NOT a version: a report's version is the
       pipeline revision it was written against, and a re-check never runs the
       pipeline, so a review fired from that button writes a report carrying the
       revision already on file.

       Nothing here clears: a finding the newest review does not raise gains a
       revision saying so and stays open. A model not mentioning something is
       not the same evidence as a rule passing. */
    recheckAiConditions: builder.mutation({
      query: ({ applicationId, since }) => ({
        url: `b2b/loans/underwriting/${applicationId}/recheck-ai-conditions`,
        method: 'POST',
        headers: getHeaders(),
        params: since ? { since } : {},
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    /* A condition the two reports did not raise. The underwriter reads the
       file and sees what neither of them can — a business gone quiet, an
       explanation that does not hold — and this is where that becomes
       something owed rather than a line in the notes box. */
    addCondition: builder.mutation({
      query: ({ applicationId, body }) => ({
        url: `b2b/loans/underwriting/${applicationId}/conditions`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    // The underwriter's reasoning. Part of the decision rather than commentary
    // beside it, so saving one is an audited event.
    saveUnderwriterNotes: builder.mutation({
      query: ({ applicationId, notes }) => ({
        url: `b2b/loans/underwriting/${applicationId}/notes`,
        method: 'PATCH',
        headers: getHeaders(),
        body: { notes },
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    /* The credit decision. Every move is made by a person — nothing infers one
       from the condition counts, so this is the only thing that writes it.
       `state` is conditions | clear_to_close | suspended | denied | pending,
       the last of which reopens a file that was stopped. */
    issueDecision: builder.mutation({
      query: ({ applicationId, state, reason }) => ({
        url: `b2b/loans/underwriting/${applicationId}/decision`,
        method: 'POST',
        headers: getHeaders(),
        body: { state, reason },
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    // Hands a cleared file to compliance. No payload — Pre-QC reads the
    // conditions and the frozen snapshot out of the underwriting tables
    // itself. This only says when, and fires their run as binding.
    sendToPreQc: builder.mutation({
      query: ({ applicationId }) => ({
        url: `b2b/loans/underwriting/${applicationId}/send-to-pre-qc`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'underwritingDesk', id: applicationId },
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    // The audit trail. Its own endpoint because the Decision tab never needs it
    // and it grows without limit.
    getUnderwritingActivity: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loans/underwriting/${applicationId}/activity`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? [],
      providesTags: (result, error, { applicationId }) => [
        { type: 'underwritingActivity', id: applicationId },
      ],
    }),

    // This is now only for manual/direct analysis, not called after /underwrite
    analyzeApplication: builder.mutation({
      query: ({ data, userId, applicationId }) => {
        return {
          url: `ai/underwriting/analyze?user_id=${userId}&application_id=${applicationId}`,
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
      invalidatesTags: (result, error, { userId, applicationId }) => [
        { type: 'underwritingResults', id: `${applicationId}-${userId}` }
      ],
    }),
  }),
});

export const {
  useGetUnderwritingResultsQuery,
  useLazyGetUnderwritingResultsQuery,
  useRunPipelineMutation,
  useGetPipelineReportQuery,
  useGetRunProgressQuery,
  useArchiveReportMutation,
  useGetArchivedReportsQuery,
  useLazyGetPipelineReportQuery,
  useAnalyzeApplicationMutation,
  useGetUnderwritingDeskQuery,
  useStartUnderwritingReviewMutation,
  useGetUnderwritingActivityQuery,
  useSaveUnderwriterNotesMutation,
  useAddConditionMutation,
  useConditionActionMutation,
  useRecheckConditionsMutation,
  useGetRecheckProgressQuery,
  useRecheckAiConditionsMutation,
  useIssueDecisionMutation,
  useSendToPreQcMutation,
} = underwritingAPI;
